import { type IssuedQuizQuote } from '../shared/utils/quizQuoteApi';

import { type QuizQuoteFormValues } from './quizQuote.schema';
import { type QuotePricingPolicy } from './quotePricingPolicy';

export type QuoteCalculationPolicy = QuotePricingPolicy;
export type FirstPurchasePromotion = NonNullable<
  QuotePricingPolicy['basicFirstPurchasePromotion']
>;

/** 체험 종료 후 7일 20% 할인 (PRD 솔루션 6). 서버 `trialEndDiscount` 의 견적용 부분집합. */
export interface TrialEndQuoteDiscount {
  discountRate: number;
  maxUserCount: number;
  maxMonthCount: number;
  /** 결제 마감. 견적 유효기간을 여기까지로 줄인다. */
  windowEnd: string;
}

/** 할인 적용 조건. 첫 `maxMonthCount`개월만 할인하고 초과분은 정가다. */
export function isTrialEndDiscountApplicable(
  discount: TrialEndQuoteDiscount | null,
  values: Pick<
    QuizQuoteFormValues,
    'plan' | 'plan_type' | 'user_count' | 'term'
  >,
): discount is TrialEndQuoteDiscount {
  return (
    discount !== null &&
    values.plan === 'pro' &&
    values.plan_type === 'individual' &&
    values.user_count <= discount.maxUserCount
  );
}

/**
 * Basic 첫 구매 할인 적용 조건. `maxDiscountMonths`를 넘는 기간은 정가로 계산한다.
 */
export function isFirstPurchaseDiscountApplicable(
  promotion: FirstPurchasePromotion | null,
  values: Pick<QuizQuoteFormValues, 'plan' | 'term'>,
): promotion is FirstPurchasePromotion {
  return promotion !== null && values.plan === 'basic';
}

function addMonths(date: string, monthCount: number): string {
  const [year, month, day] = date.split('-').map(Number);
  const targetMonth = month + monthCount;
  const targetYear = year + Math.floor((targetMonth - 1) / 12);
  const normalizedMonth = ((targetMonth - 1) % 12) + 1;
  const lastDay = new Date(
    Date.UTC(targetYear, normalizedMonth, 0),
  ).getUTCDate();

  return `${targetYear.toString().padStart(4, '0')}-${normalizedMonth
    .toString()
    .padStart(2, '0')}-${Math.min(day, lastDay).toString().padStart(2, '0')}`;
}

function addDays(issuedAt: string, days: number): string {
  const date = new Date(issuedAt);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString();
}

type QuoteDiscount = Omit<
  IssuedQuizQuote['pricing']['discounts'][number],
  'amount'
>;

function calculateDiscountedAmount({
  regularUnitAmount,
  userCount,
  term,
  discountRate,
  maxDiscountMonths,
}: {
  regularUnitAmount: number;
  userCount: number;
  term: number;
  discountRate: number;
  maxDiscountMonths: number | null;
}): number {
  const discountedMonths =
    maxDiscountMonths === null ? term : Math.min(term, maxDiscountMonths);
  const originalAmount = regularUnitAmount * userCount * term;
  const discountedBase = regularUnitAmount * userCount * discountedMonths;

  return Math.round(originalAmount - discountedBase * discountRate);
}

/**
 * 적용할 할인 하나를 고른다. 우선순위는 학교·단체 → 체험 종료 → 첫 구매이며
 * 견적 스키마상 할인은 최대 1건이다(둘을 겹쳐 주지 않는다).
 */
function resolveQuoteDiscount({
  formValues,
  policy,
  originalAmount,
  groupApplicable,
  firstPurchasePromotion,
  trialEndDiscount,
}: {
  formValues: QuizQuoteFormValues;
  policy: QuoteCalculationPolicy;
  originalAmount: number;
  groupApplicable: boolean;
  firstPurchasePromotion: FirstPurchasePromotion | null;
  trialEndDiscount: TrialEndQuoteDiscount | null;
}): { finalAmount: number; discount: QuoteDiscount | null } {
  const { user_count, term } = formValues;

  if (groupApplicable) {
    return {
      finalAmount: policy.groupUnitAmount * user_count * term,
      discount: {
        code: 'GROUP',
        label: '학교·단체 할인',
        rate: policy.groupDiscountRate,
      },
    };
  }

  if (isTrialEndDiscountApplicable(trialEndDiscount, formValues)) {
    return {
      finalAmount: calculateDiscountedAmount({
        regularUnitAmount: policy.proUnitAmount,
        userCount: user_count,
        term,
        discountRate: trialEndDiscount.discountRate,
        maxDiscountMonths: trialEndDiscount.maxMonthCount,
      }),
      discount: {
        code: 'TRIAL_END',
        label: `체험 종료 할인 (${Math.min(
          term,
          trialEndDiscount.maxMonthCount,
        )}개월)`,
        rate: trialEndDiscount.discountRate,
      },
    };
  }

  if (isFirstPurchaseDiscountApplicable(firstPurchasePromotion, formValues)) {
    return {
      finalAmount: calculateDiscountedAmount({
        regularUnitAmount: policy.basicUnitAmount,
        userCount: user_count,
        term,
        discountRate: firstPurchasePromotion.discountRate,
        maxDiscountMonths: firstPurchasePromotion.maxDiscountMonths,
      }),
      discount: {
        code: 'FIRST_PURCHASE',
        label: `첫 구매 할인 (${Math.min(
          term,
          firstPurchasePromotion.maxDiscountMonths ?? term,
        )}개월)`,
        rate: firstPurchasePromotion.discountRate,
      },
    };
  }

  return { finalAmount: originalAmount, discount: null };
}

export function createQuizQuote({
  formValues,
  policy,
  firstPurchasePromotion,
  trialEndDiscount = null,
  issuedAt,
}: {
  formValues: QuizQuoteFormValues;
  policy: QuoteCalculationPolicy;
  firstPurchasePromotion: FirstPurchasePromotion | null;
  trialEndDiscount?: TrialEndQuoteDiscount | null;
  issuedAt: string;
}): IssuedQuizQuote {
  const {
    plan,
    plan_type: requestedPricingType,
    user_count,
    term,
  } = formValues;
  const regularUnitAmount =
    plan === 'basic' ? policy.basicUnitAmount : policy.proUnitAmount;
  const originalAmount = regularUnitAmount * user_count * term;
  const groupApplicable =
    plan === 'pro' &&
    requestedPricingType === 'group' &&
    user_count >= policy.groupMinUserCount &&
    term >= policy.groupMinMonthCount;
  const pricingType = groupApplicable ? 'group' : 'individual';
  let pricingTypeNotice;
  if (requestedPricingType !== pricingType) {
    pricingTypeNotice =
      plan === 'basic'
        ? {
            code: 'GROUP_PLAN_NOT_SUPPORTED',
            message: '학교·단체 할인은 Pro 플랜에만 적용됩니다.',
          }
        : {
            code: 'GROUP_REQUIREMENTS_NOT_MET',
            message:
              '학교·단체 할인 조건을 충족하지 않아 개인 요금이 적용됐습니다.',
          };
  }

  const { finalAmount, discount } = resolveQuoteDiscount({
    formValues,
    policy,
    originalAmount,
    groupApplicable,
    firstPurchasePromotion,
    trialEndDiscount,
  });
  const discountAmount = originalAmount - finalAmount;
  const discounts: IssuedQuizQuote['pricing']['discounts'] = discount
    ? [{ ...discount, amount: discountAmount }]
    : [];

  // 할인 견적서는 결제 마감(windowEnd) 이후엔 의미가 없다 → 유효기간을 그때까지로 줄인다.
  const defaultValidUntil = addDays(issuedAt, policy.quoteValidityDays);
  const windowEndIso =
    discount?.code === 'TRIAL_END' && trialEndDiscount
      ? new Date(trialEndDiscount.windowEnd).toISOString()
      : null;
  const validUntil =
    windowEndIso !== null && windowEndIso < defaultValidUntil
      ? windowEndIso
      : defaultValidUntil;

  return {
    recipient: {
      organizationName: formValues.school_name,
      contactName: formValues.account_name,
      phone: formValues.account_phone,
      email: formValues.account_email,
      annotation: formValues.annotation || undefined,
    },
    product: {
      plan,
      requestedPricingType,
      pricingType,
      pricingTypeNotice,
      userCount: user_count,
      monthCount: term,
      startDate: formValues.start_date,
      endDate: addMonths(formValues.start_date, term),
    },
    pricing: {
      currency: 'KRW',
      regularUnitAmount,
      originalAmount,
      discountAmount,
      finalAmount,
      discounts,
    },
    issuedAt,
    validUntil,
  };
}
